'use client';

type BatchCallback<T> = (data: T) => void;

interface BatchEntry<T> {
  callback: BatchCallback<T>;
  timer: ReturnType<typeof setTimeout> | null;
  pendingFetch: (() => Promise<T>) | null;
}

class SubscriptionBatcher {
  private batches = new Map<string, BatchEntry<any>>();
  // Increased from 300ms → 600ms: coalesces rapid DB change events into a
  // single fetch, reducing unnecessary network round-trips on bulk operations.
  private readonly BATCH_DELAY = 600;

  /**
   * Schedule a debounced fetch+callback for a given channel key.
   * Multiple rapid DB change events collapse into a single fetch.
   */
  schedule<T>(
    key: string,
    fetchFn: () => Promise<T>,
    callback: BatchCallback<T>
  ): void {
    let entry = this.batches.get(key);

    if (!entry) {
      entry = { callback, timer: null, pendingFetch: null };
      this.batches.set(key, entry);
    }

    // Always update to latest fetch fn and callback
    entry.pendingFetch = fetchFn;
    entry.callback = callback;

    // Clear existing timer to reset the debounce window
    if (entry.timer !== null) {
      clearTimeout(entry.timer);
    }

    entry.timer = setTimeout(async () => {
      const current = this.batches.get(key);
      if (!current?.pendingFetch) return;
      try {
        const data = await current.pendingFetch();
        current.callback(data);
      } catch (err) {
        console.error(`SubscriptionBatcher [${key}] fetch error:`, err);
      } finally {
        if (current) {
          current.timer = null;
          current.pendingFetch = null;
        }
      }
    }, this.BATCH_DELAY);
  }

  cancel(key: string): void {
    let entry = this.batches.get(key);
    if (entry && entry.timer !== null) {
      clearTimeout(entry.timer!);
    }
    this.batches.delete(key);
  }
}

export const subscriptionBatcher = new SubscriptionBatcher();
